import { useState, useEffect, useRef } from "react";
import { Theme } from "../../../../theme";

function listToText(items: string[]): string {
  return (items ?? []).filter((s) => s.length > 0).join(", ");
}

function textToList(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function shallowEqualStringArrays(
  a: string[] | undefined,
  b: string[] | undefined
) {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) {
    return false;
  }
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) {
      return false;
    }
  }
  return true;
}

interface InputCompactListProps {
  id: string;
  value: string[] | undefined;
  onChange: (newList: string[]) => void;
  readOnly?: boolean;
  rows?: number;
  placeholder?: string;
}

export default function InputCompactList({
  id,
  value,
  onChange,
  readOnly,
  rows = 1,
  placeholder = "",
}: InputCompactListProps) {
  const [text, setText] = useState(() => listToText(value ?? []));
  const skipNextExternalSync = useRef(false);
  const prevExternalValue = useRef(value);

  useEffect(() => {
    if (skipNextExternalSync.current) {
      skipNextExternalSync.current = false;
      prevExternalValue.current = value;
      return;
    }
    if (shallowEqualStringArrays(value, prevExternalValue.current)) {
      return;
    }
    prevExternalValue.current = value;
    setText(listToText(value ?? []));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly) return;
    const newText = e.target.value;
    setText(newText);
    skipNextExternalSync.current = true;
    onChange(textToList(newText));
  };

  if (readOnly) {
    return (
      <textarea
        id={id}
        value={listToText(value ?? [])}
        readOnly
        rows={rows}
        className={`select-none ${Theme.input} h-auto min-h-[2.5rem] resize-y`}
      />
    );
  }

  return (
    <textarea
      id={id}
      value={text}
      onChange={handleChange}
      rows={rows}
      placeholder={placeholder}
      className={`select-none ${Theme.input} h-auto min-h-[2.5rem] resize-y`}
    />
  );
}
